import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './hooks/useAuth';
import { Layout } from './components/Layout';
import { LandingPage } from './pages/LandingPage';
import { useDocumentMeta } from './hooks/useDocumentMeta';
import { AuthPage } from './pages/AuthPage';
import { ForJournalistsPage } from './pages/ForJournalistsPage';
import { ForPRPage } from './pages/ForPRPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';

// Entry/landing routes stay static imports so they ship in the entry chunk
// and paint before any JS for the authenticated app downloads. Everything
// else (dashboard, settings, admin-style pages, secondary persona pages)
// is lazy-loaded so landing/auth routes don't have to wait on that bundle —
// see the LCP TODOs on /, /signin, /for-journalists, /privacy-policy.
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const PricingPage = lazy(() => import('./pages/PricingPage').then((m) => ({ default: m.PricingPage })));
const ForTradersPage = lazy(() => import('./pages/ForTradersPage').then((m) => ({ default: m.ForTradersPage })));
const ForMarketersPage = lazy(() => import('./pages/ForMarketersPage').then((m) => ({ default: m.ForMarketersPage })));
const ForFoundersPage = lazy(() => import('./pages/ForFoundersPage').then((m) => ({ default: m.ForFoundersPage })));
const ForSalesPage = lazy(() => import('./pages/ForSalesPage').then((m) => ({ default: m.ForSalesPage })));
const ForRecruitersPage = lazy(() => import('./pages/ForRecruitersPage').then((m) => ({ default: m.ForRecruitersPage })));
const ForDevelopersPage = lazy(() => import('./pages/ForDevelopersPage').then((m) => ({ default: m.ForDevelopersPage })));
const ForInvestorsPage = lazy(() => import('./pages/ForInvestorsPage').then((m) => ({ default: m.ForInvestorsPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })));
const UnsubscribedPage = lazy(() => import('./pages/UnsubscribedPage').then((m) => ({ default: m.UnsubscribedPage })));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage').then((m) => ({ default: m.TermsOfServicePage })));
const SupportPage = lazy(() => import('./pages/SupportPage').then((m) => ({ default: m.SupportPage })));
const SupportTicketPage = lazy(() => import('./pages/SupportTicketPage').then((m) => ({ default: m.SupportTicketPage })));
const ChannelsPage = lazy(() => import('./pages/ChannelsPage').then((m) => ({ default: m.ChannelsPage })));
const VerifyChannelPage = lazy(() => import('./pages/VerifyChannelPage').then((m) => ({ default: m.VerifyChannelPage })));
const SubscriptionsPage = lazy(() => import('./pages/SubscriptionsPage').then((m) => ({ default: m.SubscriptionsPage })));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const SnapshotsPage = lazy(() => import('./pages/SnapshotsPage').then((m) => ({ default: m.SnapshotsPage })));

function RouteFallback() {
  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
          <p className="text-sm text-gray-400">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Unauthed users land on the marketing landing page (which has Sign
    // in / Sign up links in its header) rather than the bare /signin
    // form — keeps the post-sign-out redirect consistent and makes
    // bookmarked /dashboard hits more welcoming for first-timers.
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AuthAwareRoot() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
      </div>
    );
  }
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

export function App() {
  useDocumentMeta();

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
      <Route path="/" element={<AuthAwareRoot />} />
      <Route path="/signin" element={<AuthPage />} />
      <Route path="/signup" element={<AuthPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      {/* FE1 — lifecycle unsubscribe outcomes. The Lambda 302s here; */}
      {/* both outcomes render so the endpoint is not an existence oracle. */}
      <Route path="/unsubscribed" element={<UnsubscribedPage ok />} />
      <Route path="/unsubscribe-error" element={<UnsubscribedPage ok={false} />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      {/* Pricing is intentionally public — high search-intent landing page
          for prospective customers comparing plans. PricingPage handles
          unauthenticated state internally (read-only mode + signup CTA). */}
      <Route path="/pricing" element={<PricingPage />} />
      {/* Persona landing pages — high-intent, indexable, public. Each one
          targets a distinct buyer-keyword cluster (journalists, traders,
          marketing teams) and links to /signup, /pricing, and the matching
          deep-dive blog post. */}
      <Route path="/for-journalists" element={<ForJournalistsPage />} />
      <Route path="/for-traders" element={<ForTradersPage />} />
      <Route path="/for-marketers" element={<ForMarketersPage />} />
      <Route path="/for-founders" element={<ForFoundersPage />} />
      <Route path="/for-sales" element={<ForSalesPage />} />
      <Route path="/for-recruiters" element={<ForRecruitersPage />} />
      <Route path="/for-developers" element={<ForDevelopersPage />} />
      <Route path="/for-investors" element={<ForInvestorsPage />} />
      <Route path="/for-pr" element={<ForPRPage />} />
      <Route path="/login" element={<Navigate to="/signin" replace />} />
      {/* Legacy routes redirect — Custom Auth replaces email verification
          and password reset with the single OTP flow on AuthPage. */}
      <Route path="/verify-email" element={<Navigate to="/signin" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/signin" replace />} />

      {/* Docs served by VitePress at /docs/ — not part of the React SPA */}

      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/channels" element={<ChannelsPage />} />
        <Route path="/channels/:channelId/verify" element={<VerifyChannelPage />} />
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/snapshots" element={<SnapshotsPage />} />
        <Route path="/api-keys" element={<ApiKeysPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/support/:ticketId" element={<SupportTicketPage />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}
